import { useState, useEffect } from 'react';
import { useInstall } from '@/contexts/InstallContext';
import { Download, Share, X, ExternalLink, Plus, CheckCircle } from 'lucide-react';
import { isAndroid, isTWASession } from '@/utils/webviewDetect';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.wnapp.id1780845607573&utm_source=bodymax_web';

function isIOSNotSafari() {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
  if (!isIOS) return false;
  // In Safari: has Safari but NOT CriOS/FxiOS/etc
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return !isSafari;
}

export function DashboardInstallCard({ hasCompletedOnboarding, hasActivePlan }) {
  const { canInstallNative, isIOSSafari, installed, triggerInstall, dismiss, shouldShowPrompt } = useInstall();
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [step, setStep] = useState(0); // 0=overview, 1=step-by-step guide

  useEffect(() => {
    const check = () => setVisible(shouldShowPrompt(hasCompletedOnboarding, hasActivePlan));
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [shouldShowPrompt, hasCompletedOnboarding, hasActivePlan]);

  if (!visible || installed) return null;

  const handleDismiss = () => {
    dismiss();
    setVisible(false);
  };

  const handleInstall = async () => {
    setInstalling(true);
    const accepted = await triggerInstall();
    if (!accepted) dismiss();
    setInstalling(false);
    setVisible(false);
  };

  // ── iOS but NOT Safari (Chrome, Instagram, etc) — tell them to open in Safari ──
  if (isIOSNotSafari()) {
    return (
      <div className="rounded-2xl bg-[#141414] border-2 border-accent p-5" data-testid="install-card-ios-nonsafari">
        <button onClick={handleDismiss} className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-[#1F1F1F] active:scale-90">
          <X size={14} className="text-[#A1A1AA]" />
        </button>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center shrink-0">
            <ExternalLink size={18} className="text-accent" />
          </div>
          <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white">Open in Safari</h3>
        </div>
        <p className="text-sm text-[#A1A1AA] mb-4">
          To install BODYMAX as an app, open this page in <strong className="text-white">Safari</strong> first.
        </p>
        <button
          onClick={() => {
            navigator.clipboard?.writeText(window.location.origin);
            alert('Link copied! Open Safari and paste it in the address bar.');
          }}
          className="w-full py-3 rounded-xl bg-accent text-accent-foreground font-heading font-bold uppercase tracking-wider text-sm active:scale-[0.97] transition-all"
          data-testid="copy-link-safari-btn"
        >
          Copy Link for Safari
        </button>
      </div>
    );
  }

  // ── IPHONE SAFARI — step-by-step install guide ──
  if (isIOSSafari && !canInstallNative) {
    return (
      <div className="relative rounded-2xl bg-[#141414] border-2 border-accent overflow-hidden" data-testid="install-card-ios">
        <button onClick={handleDismiss} className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-[#1F1F1F] active:scale-90" data-testid="install-dismiss-btn">
          <X size={14} className="text-[#A1A1AA]" />
        </button>

        {step === 0 ? (
          <div className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center shrink-0">
                <Download size={18} className="text-accent" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white">Install App</h3>
                <p className="text-xs text-[#A1A1AA]">Get the full app experience</p>
              </div>
            </div>
            <button
              onClick={() => setStep(1)}
              className="w-full py-3 rounded-xl bg-accent text-accent-foreground font-heading font-bold uppercase tracking-wider text-sm active:scale-[0.97] transition-all"
              data-testid="install-show-steps-btn"
            >
              Show Me How
            </button>
          </div>
        ) : (
          <div className="p-5">
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-4">Install in 3 Steps</h3>
            
            <div className="space-y-4">
              {/* Step 1 */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center shrink-0 text-accent-foreground font-heading font-bold text-sm">1</div>
                <div>
                  <p className="text-base font-bold text-white">Tap the Share button</p>
                  <p className="text-sm text-[#A1A1AA] mt-0.5">
                    Find the <Share size={14} className="inline -mt-0.5 mx-1 text-accent" /> icon at the bottom of Safari
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center shrink-0 text-accent-foreground font-heading font-bold text-sm">2</div>
                <div>
                  <p className="text-base font-bold text-white">Scroll down & tap</p>
                  <p className="text-sm text-[#A1A1AA] mt-0.5">
                    <Plus size={14} className="inline -mt-0.5 mr-1 text-accent" /><strong className="text-white">Add to Home Screen</strong>
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center shrink-0 text-accent-foreground font-heading font-bold text-sm">3</div>
                <div>
                  <p className="text-base font-bold text-white">Tap "Add"</p>
                  <p className="text-sm text-[#A1A1AA] mt-0.5">BODYMAX will appear on your home screen</p>
                </div>
              </div>
            </div>

            <button
              onClick={handleDismiss}
              className="w-full mt-5 py-3 rounded-xl bg-accent text-accent-foreground font-heading font-bold uppercase tracking-wider text-sm active:scale-[0.97] transition-all"
              data-testid="install-gotit-btn"
            >
              <CheckCircle size={16} className="inline mr-2 -mt-0.5" /> Got It
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── ANDROID (not in Play Store app) — redirect to Google Play ──
  if (isAndroid && !isTWASession) {
    return (
      <div
        className="relative rounded-2xl overflow-hidden bg-card border border-border"
        data-testid="install-card-android"
      >
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-muted/50 active:scale-90 z-10"
          data-testid="install-dismiss-btn"
        >
          <X size={14} className="text-muted-foreground" />
        </button>

        <div className="flex items-center gap-4 p-4">
          {/* App icon */}
          <img
            src="/icon-192.png"
            alt="BODYMAX"
            className="w-14 h-14 rounded-xl shrink-0"
          />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold text-foreground leading-tight">BODYMAX.FIT</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">Your Personal Trainer</p>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center mt-2.5"
              data-testid="install-playstore-btn"
            >
              <img
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
                className="h-10 -ml-2"
              />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── DESKTOP — native PWA install ──
  if (canInstallNative) {
    return (
      <div
        className="relative rounded-2xl p-5 overflow-hidden bg-card border border-border"
        data-testid="install-card-android"
      >
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-muted active:scale-90"
          data-testid="install-dismiss-btn"
        >
          <X size={14} className="text-muted-foreground" />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
            <Download size={18} className="text-accent" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-foreground mb-1">Install BODYMAX</h3>
            <p className="text-xs leading-relaxed mb-3 text-muted-foreground">
              Add BODYMAX to your device for faster access and a full-screen app experience.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleInstall}
                disabled={installing}
                className="px-5 py-2 rounded-full text-xs font-bold bg-accent text-accent-foreground active:scale-95 transition-transform disabled:opacity-50"
                data-testid="install-accept-btn"
              >
                {installing ? 'Installing...' : 'Install'}
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 rounded-full text-xs font-medium text-muted-foreground active:scale-95"
                data-testid="install-notnow-btn"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
